import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Bell, X, Check, Gift, ListTodo, CheckCircle, Package, BellOff, Loader2 } from 'lucide-react';
import usePushNotifications from '../hooks/usePushNotifications';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to get a random phrase from an array
const getRandomPhrase = (phrases) => {
  if (!phrases || !Array.isArray(phrases) || phrases.length === 0) return '';
  return phrases[Math.floor(Math.random() * phrases.length)];
};

// Helper function to get translated notification message
const getNotificationMessage = (notification, t) => {
  const data = notification.data || {};
  
  switch (notification.type) {
    case 'task_created':
      return t('notifications.taskCreated', {
        actor: data.actor_name || 'Someone',
        task: data.task_title || 'a task',
        list: data.list_name || 'a list'
      });
    case 'task_updated':
      return t('notifications.taskUpdated', {
        actor: data.actor_name || 'Someone',
        task: data.task_title || 'a task',
        list: data.list_name || 'a list'
      });
    case 'task_completed': {
      const baseMessage = t('notifications.taskCompleted', {
        actor: data.actor_name || 'Someone',
        task: data.task_title || 'a task',
        points: data.points || 0
      });
      const phrases = t('notifications.motivationalPhrases', { returnObjects: true });
      const motivationalPhrase = getRandomPhrase(phrases);
      return `${baseMessage} ${motivationalPhrase}`;
    }
    case 'list_shared':
      return t('notifications.listShared', {
        actor: data.actor_name || 'Someone',
        list: data.list_name || 'a list'
      });
    case 'user_unsubscribed':
      return t('notifications.userLeft', {
        actor: data.actor_name || 'Someone',
        list: data.list_name || 'a list'
      });
    case 'reward_redeemed': {
      const baseMessage = t('notifications.rewardRedeemed', {
        actor: data.actor_name || 'Someone',
        reward: data.reward_title || 'a reward',
        points: data.cost_points || 0
      });
      const phrases = t('notifications.congratsPhrases', { returnObjects: true });
      const congratsPhrase = getRandomPhrase(phrases);
      return `${baseMessage} ${congratsPhrase}`;
    }
    case 'reward_delivered':
      return t('notifications.rewardDelivered', {
        actor: data.actor_name || 'Someone',
        reward: data.reward_title || 'a reward'
      });
    default:
      // Fallback to the original message if no translation available
      return notification.message;
  }
};

const getNotificationIcon = (type) => {
  switch(type) {
    case 'task_added':
      return <ListTodo className="w-5 h-5 text-[#FF8BA7]" />;
    case 'task_completed':
      return <CheckCircle className="w-5 h-5 text-[#ADE498]" />;
    case 'reward_redeemed':
      return <Gift className="w-5 h-5 text-[#FFC247]" />;
    case 'reward_delivered':
      return <Package className="w-5 h-5 text-[#ADE498]" />;
    default:
      return <Bell className="w-5 h-5 text-zinc-500" />;
  }
};

export default function NotificationBell() {
  const { t, i18n } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [showPanel, setShowPanel] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Push notifications hook
  const {
    isSubscribed,
    isSupported,
    isLoading: pushLoading,
    toggleSubscription,
    permission
  } = usePushNotifications();

  // Push notification messages
  const pushMessages = {
    es: { enabled: 'Notificaciones push activadas', disabled: 'Notificaciones push desactivadas', denied: 'Bloqueadas en navegador' },
    en: { enabled: 'Push notifications enabled', disabled: 'Push notifications disabled', denied: 'Blocked in browser' },
    gl: { enabled: 'Notificacións push activadas', disabled: 'Notificacións push desactivadas', denied: 'Bloqueadas no navegador' },
    ca: { enabled: 'Notificacions push activades', disabled: 'Notificacions push desactivades', denied: 'Bloquejades al navegador' },
    zh: { enabled: '推送通知已启用', disabled: '推送通知已禁用', denied: '在浏览器中被阻止' }
  };
  const pushMsg = pushMessages[i18n.language] || pushMessages.es;

  const getLocale = () => {
    const localeMap = { es: 'es-ES', en: 'en-US', gl: 'gl-ES', ca: 'ca-ES', zh: 'zh-CN' };
    return localeMap[i18n.language] || 'es-ES';
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/read`, {}, { withCredentials: true });
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, { withCredentials: true });
      fetchNotifications();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        onClick={() => setShowPanel(!showPanel)}
        data-testid="notification-bell"
        className="relative rounded-full border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-800 p-2 hover:bg-zinc-100 dark:hover:bg-zinc-700 hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] transition-all"
      >
        <Bell className="w-5 h-5 text-zinc-900 dark:text-white" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-[#FF8BA7] border-2 border-[#18181B] text-[#18181B] text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      {showPanel && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setShowPanel(false)}
          />
          <div 
            className="fixed sm:absolute inset-x-0 bottom-0 sm:inset-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 sm:max-w-[calc(100vw-2rem)] bg-white dark:bg-zinc-800 border-t-2 sm:border-2 border-[#18181B] dark:border-zinc-600 rounded-t-2xl sm:rounded-2xl hard-shadow z-50 max-h-[70vh] sm:max-h-[80vh] overflow-hidden flex flex-col"
            data-testid="notification-panel"
          >
            {/* Header */}
            <div className="p-3 sm:p-4 border-b-2 border-[#18181B] dark:border-zinc-600">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-base sm:text-lg text-zinc-900 dark:text-white">{t('notifications.title')}</h3>
                <div className="flex gap-2">
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="text-[10px] sm:text-xs px-2 sm:px-3 py-1 rounded-full border-2 border-[#18181B] bg-[#ADE498] hover:bg-[#bdeaa8] font-bold transition-colors"
                      data-testid="mark-all-read"
                    >
                      {t('notifications.markAll')}
                    </button>
                  )}
                  <button
                    onClick={() => setShowPanel(false)}
                    className="p-1 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-700"
                  >
                    <X className="w-4 h-4 text-zinc-900 dark:text-white" />
                  </button>
                </div>
              </div>
              
              {/* Push Notification Toggle */}
              {isSupported && (
                <div className="flex items-center justify-between py-2 px-3 bg-zinc-50 dark:bg-zinc-700 rounded-lg">
                  <div className="flex items-center gap-2">
                    {permission === 'denied' ? (
                      <BellOff className="w-4 h-4 text-red-500" />
                    ) : isSubscribed ? (
                      <Bell className="w-4 h-4 text-[#22c55e]" />
                    ) : (
                      <BellOff className="w-4 h-4 text-zinc-400" />
                    )}
                    <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
                      {permission === 'denied' 
                        ? pushMsg.denied 
                        : (isSubscribed ? pushMsg.enabled : pushMsg.disabled)}
                    </span>
                  </div>
                  <button
                    onClick={toggleSubscription}
                    disabled={pushLoading || permission === 'denied'}
                    className={`relative w-11 h-6 rounded-full border-2 border-[#18181B] dark:border-zinc-600 transition-colors ${
                      permission === 'denied' 
                        ? 'bg-zinc-200 dark:bg-zinc-600 cursor-not-allowed' 
                        : isSubscribed 
                          ? 'bg-[#22c55e]' 
                          : 'bg-zinc-300 dark:bg-zinc-600'
                    }`}
                    data-testid="push-toggle-switch"
                  >
                    <span 
                      className={`absolute top-0.5 w-4 h-4 rounded-full bg-white border border-[#18181B] dark:border-zinc-600 transition-transform ${
                        isSubscribed ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    >
                      {pushLoading && (
                        <Loader2 className="w-3 h-3 animate-spin text-zinc-500 absolute top-0.5 left-0.5" />
                      )}
                    </span>
                  </button>
                </div>
              )}
            </div>

            {/* Notifications List */}
            <div className="overflow-y-auto flex-1">
              {notifications.length === 0 ? (
                <div className="p-6 sm:p-8 text-center text-zinc-400 dark:text-zinc-500">
                  <Bell className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm sm:text-base">{t('notifications.noNotifications')}</p>
                </div>
              ) : (
                <div className="divide-y divide-zinc-200 dark:divide-zinc-700">
                  {notifications.map((notification) => (
                    <div
                      key={notification.notification_id}
                      data-testid={`notification-${notification.notification_id}`}
                      className={`p-3 sm:p-4 transition-colors ${
                        !notification.read 
                          ? 'bg-[#ADE498]/20 dark:bg-[#ADE498]/10 hover:bg-[#ADE498]/30 dark:hover:bg-[#ADE498]/20 border-l-4 border-l-[#ADE498]' 
                          : 'bg-zinc-50 dark:bg-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-700 border-l-4 border-l-zinc-300 dark:border-l-zinc-600'
                      }`}
                    >
                      <div className="flex items-start gap-2 sm:gap-3">
                        <div className="flex-shrink-0 mt-0.5 sm:mt-1">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs sm:text-sm font-medium ${
                            !notification.read ? 'text-[#18181B] dark:text-white' : 'text-zinc-500 dark:text-zinc-400'
                          }`}>
                            {getNotificationMessage(notification, t)}
                          </p>
                          <p className="text-[10px] sm:text-xs text-zinc-400 dark:text-zinc-500 mt-0.5 sm:mt-1">
                            {new Date(notification.created_at).toLocaleDateString(getLocale(), {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        {!notification.read && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsRead(notification.notification_id);
                            }}
                            className="p-1 sm:p-1.5 rounded-lg bg-[#ADE498] hover:bg-[#bdeaa8] transition-colors flex-shrink-0 border-2 border-[#18181B]"
                            data-testid={`mark-read-${notification.notification_id}`}
                          >
                            <Check className="w-3 h-3 sm:w-4 sm:h-4 text-[#18181B]" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
